import React, { useState, useCallback, useMemo } from 'react';
import { Alert, Box, Button, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useIntl } from 'react-intl';
import { FileUploadArea, ConfirmDialogWithTextInput } from 'app/components';
import { FileType } from 'app/components/FileUploadArea/FileUploadArea.types';
import { TranslationKeys } from 'app/translations';
import { useSnakeBar } from 'app/hooks';
import { useBulkLeftCompanyBackupMutation, useBulkLeftCompanyUpdateMutation } from 'app/hooks/api/admin';
import type { BulkLeftCompanyEmployee, BulkLeftCompanyBackupResponse } from '../../Services';
import { CSV_TEMPLATE_HEADERS } from './types';
import type { ParseError } from './types';
import { parseCsv } from './parseCsv';
import { BackupPreview } from './BackupPreview';

// ─── Page ─────────────────────────────────────────────────────────────────────

const STEPS = [
  TranslationKeys.adminPanel_bulkLeftCompany_step_upload,
  TranslationKeys.adminPanel_bulkLeftCompany_step_review,
  TranslationKeys.adminPanel_bulkLeftCompany_step_confirm,
] as const;

const CONFIRM_SOLUTION = 'CONFIRM';

export const BulkLeftCompanyPage = () => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const [employees, setEmployees] = useState<BulkLeftCompanyEmployee[]>([]);
  const [backupData, setBackupData] = useState<BulkLeftCompanyBackupResponse | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: loadBackup, isLoading: isLoadingBackup } = useBulkLeftCompanyBackupMutation();
  const { mutateAsync: applyUpdate } = useBulkLeftCompanyUpdateMutation();

  // ── Template download ──────────────────────────────────────────────────────────────

  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE_HEADERS], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-left-company-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // ── Step 0: CSV upload ──────────────────────────────────────────────────────

  const handleFileUpload = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setCsvFile(file);
    setParseErrors([]);
    setEmployees([]);
    setBackupData(null);
    setBackupError(null);

    const result = await parseCsv(file);
    if (result.ok) {
      setEmployees(result.employees);
    } else {
      setParseErrors(result.errors);
    }
  }, []);

  const handleLoadPreview = useCallback(async () => {
    if (!employees.length) return;
    setBackupError(null);

    try {
      const response = await loadBackup(employees);
      setBackupData(response.data);
      setActiveStep(1);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_backupError });
      setBackupError(message);
    }
  }, [employees, loadBackup, intl]);

  // ── Step 1: Review ──────────────────────────────────────────────────────────

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  // ── Step 2: Confirm & apply ─────────────────────────────────────────────────

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await applyUpdate(employees);
      showSuccessSnakeBar({
        message: intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submitSuccess }),
      });
      setActiveStep(2);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submitError });
      showErrorSnakeBar({ message });
    } finally {
      setIsSubmitting(false);
    }
  }, [applyUpdate, employees, intl, showSuccessSnakeBar, showErrorSnakeBar]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setCsvFile(null);
    setParseErrors([]);
    setEmployees([]);
    setBackupData(null);
    setBackupError(null);
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const canLoadPreview = useMemo(
    () => employees.length > 0 && parseErrors.length === 0 && !isLoadingBackup,
    [employees.length, parseErrors.length, isLoadingBackup],
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom fontWeight={700}>
        {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_title })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_description })}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(stepKey => (
          <Step key={stepKey}>
            <StepLabel>{intl.formatMessage({ id: stepKey })}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── Step 0: Upload ─────────────────────────────────────────────────── */}
      {activeStep === 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_uploadHint })}
            </Typography>
            <Button size="small" variant="outlined" onClick={handleDownloadTemplate}>
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_downloadTemplate })}
            </Button>
          </Box>

          <FileUploadArea
            maxSize={10 * 1024 * 1024}
            onChange={handleFileUpload}
            types={[FileType.Csv]}
            filesLimit={1}
            showSuccessMessage={false}
          />

          {csvFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_fileSummary },
                { fileName: csvFile.name, count: employees.length },
              )}
            </Typography>
          )}

          {parseErrors.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {parseErrors.map((e, i) => {
                let message: string;
                if (e.kind === 'badHeaders') {
                  message = intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_badHeaders });
                } else if (e.kind === 'badColumnCount') {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_badColumnCount },
                    { count: e.count },
                  );
                } else if (e.kind === 'missingField') {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidRow },
                    { row: e.row, field: e.field },
                  );
                } else if (e.kind === 'invalidUuid') {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidUuid },
                    { row: e.row, value: e.value },
                  );
                } else if (e.kind === 'invalidBsn') {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidBsn },
                    { row: e.row, value: e.value },
                  );
                } else if (e.kind === 'invalidDate') {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidDate },
                    { row: e.row, value: e.value },
                  );
                } else {
                  message = intl.formatMessage(
                    { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidStartDate },
                    {
                      row: (e as { row: number; value: string }).row,
                      value: (e as { row: number; value: string }).value,
                    },
                  );
                }
                return (
                  <Alert key={i} severity="error" sx={{ py: 0 }}>
                    {message}
                  </Alert>
                );
              })}
            </Box>
          )}

          {employees.length === 0 && csvFile && parseErrors.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_noRows })}
            </Alert>
          )}

          {backupError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {backupError}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!canLoadPreview}
              endIcon={
                isLoadingBackup ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FontAwesomeIcon icon={faArrowRight} />
                )
              }
              onClick={handleLoadPreview}
            >
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_uploadPreview })}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Step 1: Review ─────────────────────────────────────────────────── */}
      {activeStep === 1 && backupData && (
        <Box>
          <Typography variant="h6" gutterBottom>
            {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_title })}
          </Typography>

          <BackupPreview data={backupData} csvEmployees={employees} />

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={handleReset}>
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_step_upload })}
            </Button>

            <Button
              variant="contained"
              color="warning"
              disabled={isSubmitting}
              endIcon={
                isSubmitting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faArrowRight} />
              }
              onClick={handleOpenConfirm}
            >
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submit })}
            </Button>
          </Box>

          <ConfirmDialogWithTextInput
            open={isConfirmOpen}
            setOpen={setIsConfirmOpen}
            title={intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_title })}
            solution={CONFIRM_SOLUTION}
            label={intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_inputLabel })}
            errorMessage={intl.formatMessage({
              id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_inputError,
            })}
            onConfirm={handleConfirm}
          >
            <Typography variant="body2">
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_confirmDialog_content })}
            </Typography>
          </ConfirmDialogWithTextInput>
        </Box>
      )}

      {/* ── Step 2: Success ─────────────────────────────────────────────────── */}
      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <FontAwesomeIcon icon={faCheck} size="3x" color="#40C484" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submitSuccess })}
          </Typography>
          <Button sx={{ mt: 3 }} variant="outlined" onClick={handleReset}>
            {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_step_upload })}
          </Button>
        </Box>
      )}
    </Box>
  );
};
